import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, History, Calendar, FileText, FileUp, PenLine,
  CheckCircle, AlertCircle, Droplets, ChevronDown, FlaskConical, Save
} from 'lucide-react';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEST_KEYS_MAP = {
  hemograma: 'Hemograma Completo', hematocrito: 'Hematocrito', hemoglobina: 'Hemoglobina',
  plaquetas: 'Plaquetas', leucocitos: 'Leucocitos', glucosa: 'Glucosa',
  colesterol_total: 'Colesterol Total', trigliceridos: 'Trigliceridos', creatinina: 'Creatinina',
  acido_urico: 'Acido Urico', alt: 'ALT (SGPT)', ast: 'AST (SGOT)', tsh: 'TSH',
  hemoglobina_glicosilada: 'HbA1C', c_hdl: 'C-HDL', c_ldl: 'C-LDL',
  bun: 'BUN', bilirrubina: 'Bilirrubina Total', hierro: 'Hierro',
  ferritina: 'Ferritina', vitamina_d: 'Vitamina D', vitamina_b12: 'Vitamina B12',
  testosterona: 'Testosterona', insulina: 'Insulina', cortisol_am: 'Cortisol AM',
};

const ClientUploadsListPage = () => {
  const navigate = useNavigate();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/blood-analytics/my-uploads`).then(res => {
      setUploads(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-teal-100 border-t-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in pb-4" data-testid="uploads-list-page">
      <button onClick={() => navigate('/client/analytics')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="back-btn">
        <ArrowLeft size={16} /> Analiticas de Sangre
      </button>
      <div className="pt-1">
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Historial de Pruebas
        </h1>
        <p className="text-sm text-gray-400 mt-1 font-medium">Tus estudios subidos organizados por fecha</p>
      </div>

      {/* Count */}
      <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-2xl p-4 text-white shadow-lg shadow-teal-500/20 flex items-center gap-3">
        <History size={20} />
        <p className="font-bold text-sm">
          {uploads.length === 0 ? 'Sin estudios registrados' : `${uploads.length} estudio${uploads.length > 1 ? 's' : ''} registrado${uploads.length > 1 ? 's' : ''}`}
        </p>
      </div>

      {uploads.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center" data-testid="no-uploads">
          <div className="w-20 h-20 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Droplets size={32} className="text-teal-300" />
          </div>
          <p className="font-bold text-gray-700 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Sin estudios</p>
          <p className="text-sm text-gray-400 mt-1">Sube tu primera analitica de sangre</p>
        </div>
      ) : (
        <div className="space-y-3">
          {uploads.map((item, idx) => {
            const filledCount = Object.values(item.results || {}).filter(v => v.value).length;
            const isCompleted = item.status === 'completed';
            const isPdf = item.source === 'pdf_upload';
            const isExpanded = expandedId === item.id;
            const resultEntries = Object.entries(item.results || {}).filter(([, v]) => v.value);

            return (
              <div
                key={item.id}
                className="bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
                style={{ animationDelay: `${idx * 60}ms` }}
                data-testid={`upload-card-${item.id}`}
              >
                <div className={`h-1 bg-gradient-to-r ${isCompleted ? 'from-emerald-400 to-teal-500' : 'from-amber-400 to-orange-500'}`} />
                <button onClick={() => setExpandedId(isExpanded ? null : item.id)} className="w-full p-4 text-left">
                  <div className="flex items-start gap-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isCompleted ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                      {isPdf ? <FileText size={20} className={isCompleted ? 'text-emerald-500' : 'text-amber-500'} /> : <PenLine size={20} className={isCompleted ? 'text-emerald-500' : 'text-amber-500'} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 text-sm truncate">{item.template_name || item.original_filename || 'Analitica'}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <div className="flex items-center gap-1.5">
                          <Calendar size={12} className="text-gray-400" />
                          <span className="text-xs text-gray-500 font-medium">{item.prescription_date}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Droplets size={12} className="text-gray-400" />
                          <span className="text-xs text-gray-500 font-medium">{filledCount} resultados</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${isPdf ? 'bg-violet-50 text-violet-600' : 'bg-blue-50 text-blue-600'}`}>
                          {isPdf ? 'PDF + IA' : 'Manual'}
                        </span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${isCompleted ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                          {isCompleted ? 'Completo' : 'Pendiente'}
                        </span>
                      </div>
                    </div>
                    <ChevronDown size={16} className={`text-gray-400 flex-shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {isExpanded && resultEntries.length > 0 && (
                  <div className="border-t border-gray-100 p-4 space-y-2 animate-fade-in bg-gray-50/30" data-testid={`upload-detail-${item.id}`}>
                    {resultEntries.map(([key, val]) => (
                      <div key={key} className="flex items-center justify-between py-1.5 px-2">
                        <span className="text-sm text-gray-600 font-medium">{TEST_KEYS_MAP[key] || key}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{val.value}</span>
                          {val.unit && <span className="text-[10px] text-gray-400">{val.unit}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientUploadsListPage;
